import { useMemo, useState } from "react"

import { useInfiniteQuery } from "@tanstack/react-query"

import { allDocuments } from "@/components/job-postings/KanbanStageConfig"
import { supabase } from "@/lib/supabase"
import { applicationFilterEmbeds, applyApplicationFilters } from "@/services/applicationFilters"
import { Application, FilterState } from "@/types/application"
import { findEarliestBooking, selectActiveBookings } from "@/utils/sessionBookings"

interface FetchApplicationsOptions {
  stage: string
  limit?: number
  searchQuery?: string
  filters?: Partial<FilterState>
}

interface ApplicationsResponse {
  applications: Application[]
  nextCursor: number | null
  totalCount: number
}

// Define types for the Supabase response structure
type ApplicantData = {
  id: string
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  phone_number?: string | null
  city?: string | null
  has_electric_van?: boolean | null
  needs_rental_van?: boolean | null
  address_line_1?: string | null
  postcode?: string | null
}

type DepotData = {
  id: string
  name?: string | null
  address?: string | null
  city?: string | null
}

type BookingRow = {
  application_id: string | null
  date?: string | null
  attended?: boolean | null
  cancelled_at?: string | null
  session?: { type?: string | null } | null
}

type Assessment = {
  id: string
  application_id: string
  type: string | null
  started_at: string | null
  completed_at: string | null
  result: string | null
  score: number | null
  question_num: number | null
}

type Document = {
  id: string
  status: string | null
  application_id: string
  type: string | null
}

type StageHistory = {
  stage: string
  moved_at: string
}

type TrialBlock = {
  id: string
  application_id: string
  trial_id: string | null
  date: string | null
  route_code: string | null
  route_cycle: string | null
  location: string | null
  result: string | null
  additional_trial: boolean | null
  comments: string | null
  delivery_pictures: string | null
  delivery_notes: string | null
  instruction_compliance: string | null
  delivery_success_score: string | null
  driving_behaviour: string | null
  vehicle_handling: string | null
  safety_score: number | null
  customer_feedback: string | null
  issue_resolution: string | null
  time_management: string | null
  load_check: string | null
  vehicle_name: string | null
  review_status: string | null
  attended: boolean | null
  reviewed_at: string | null
}

// The actual structure returned by Supabase when using foreign table selection
interface SupabaseRawRecord {
  id: string
  application_id?: string | null
  current_stage?: string | null
  current_segment?: string | null
  contract_salary?: number | null
  status?: string | null
  created_at?: string | null
  updated_at?: string | null
  applicant: ApplicantData | ApplicantData[] | null
  changed_stage_at?: string | null
  stage_reason: string | null
  stage_note: string | null
  person_id: string | null
  person?: { person_id: string | null } | null
  job:
    | {
        id: string
        title?: string | null
        vehicle_type?: string | null
        stages?: string[] | null
        data_collection_only?: boolean | null
        depot: DepotData | DepotData[] | null
      }
    | Array<{
        id: string
        title?: string | null
        vehicle_type?: string | null
        stages?: string[] | null
        data_collection_only?: boolean | null
        depot: DepotData | DepotData[] | null
      }>
    | null
  session_bookings?: BookingRow[] | null
  trial_blocks?: TrialBlock[] | null
  assessments?: Assessment[] | null
  documents?: Document[] | null
  stage_history?: StageHistory[] | null
}

export const useApplicationsByStage = ({
  stage,
  limit = 10,
  searchQuery = "",
  filters = {},
}: FetchApplicationsOptions) => {
  const [loading, setLoading] = useState(false)

  // Function to fetch applications by stage with pagination
  const fetchApplicationsByStage = async ({ pageParam = 0 }): Promise<ApplicationsResponse> => {
    try {
      setLoading(true)
      const selectColumns = `
          id,
          application_id,
          current_stage,
          current_segment,
          contract_salary,
          status,
          created_at,
          updated_at,
          changed_stage_at,
          stage_note,
          stage_reason,
          applicant:applicant_id(id, first_name, last_name, email, phone_number, address_line_1, city, postcode, has_electric_van, needs_rental_van),
          job:job_id(id, title, vehicle_type, stages, data_collection_only, depot:depot_id(id, name, address, city)),
          session_bookings:session_bookings(application_id, date, attended, cancelled_at, session:session_id(type)),
          assessments(id, application_id, type, started_at, completed_at, score, result),
          documents(application_id, type, status),
          stage_history:application_stage_history(application_id, stage, moved_at),
          ${applicationFilterEmbeds(filters, searchQuery)}
          person_id,
          person:people!applications_person_id_fkey(person_id),
          trial_blocks(
            id,
            application_id
          )
        `

      let query = supabase
        .from("applications")
        .select(selectColumns, { count: "estimated" })
        .order("changed_stage_at", { ascending: false, nullsFirst: false })

      if (stage && stage !== "all") {
        query = query.eq("current_stage", stage)
      }

      query = applyApplicationFilters(query, filters, searchQuery)

      // Apply pagination
      const { data, error, count } = await query
        .range(pageParam, pageParam + limit - 1)
        .limit(limit)
      if (error) {
        console.error("Error fetching applications:", error)
        throw new Error(error.message)
      }

      // Map database rows for further processing
      const rows = (data || []) as unknown as SupabaseRawRecord[]

      // No separate booking fetch needed; bookings are joined when stage === 'orientation'

      // Map database fields to our Application interface
      const applications = rows.map((app: SupabaseRawRecord) => {
        // Create safe typed objects with default values
        const applicantData = Array.isArray(app.applicant) ? app.applicant[0] : app.applicant
        const jobData = Array.isArray(app.job) ? app.job[0] : app.job

        // Safely access nested properties with defaults
        const depotData =
          jobData && jobData.depot
            ? Array.isArray(jobData.depot)
              ? jobData.depot[0]
              : jobData.depot
            : null

        // Create safe objects with default values
        const applicant = applicantData || {
          first_name: "",
          last_name: "",
          email: "",
          phone_number: "",
          has_electric_van: false,
        }

        const job = jobData || {
          id: "",
          title: "",
          stages: [],
          data_collection_only: null,
        }
        const depot = depotData || { id: "", name: "", address: "", city: "" }

        // Compute booking flags from nested bookings for stage-specific UI
        let orientationBooked: boolean | undefined = undefined
        let orientationBookingDate: string | null | undefined = undefined
        let orientationAttended: boolean | null | undefined = undefined
        let vehicleCollectionBooked: boolean | undefined = undefined
        let vehicleCollectionBookingDate: string | null | undefined = undefined
        let vehicleCollectionAttended: boolean | null | undefined = undefined

        if (stage === "orientation") {
          const bookings = (app.session_bookings || []) as BookingRow[]
          const orientationBookings = selectActiveBookings(bookings, "orientation")
          orientationBooked = orientationBookings.length > 0
          const earliestBooking = findEarliestBooking(orientationBookings)
          orientationBookingDate = earliestBooking?.date ?? null
          orientationAttended = earliestBooking?.attended ?? null
        }

        let requiredDocs: string[] = []
        let uploadedCount = 0
        let verifiedCount = 0
        let allUploaded = false
        let totalRequiredDocs = 0
        let requiredDocsDetailed: {
          id: string
          title: string
          uploaded: boolean
        }[] = []
        let uploadedDocsDetailed: {
          id: string
          title: string
          status: string | null
        }[] = []

        if (stage === "orientation") {
          requiredDocs = getRequiredDocs(app)
          const docs = app.documents || []

          const uploadedDocs = docs.map((d) => d.type ?? "")

          uploadedCount = requiredDocs.filter((d) => uploadedDocs.includes(d)).length
          verifiedCount = docs.filter(
            (d) => d.status?.toLowerCase() === "verified" && requiredDocs.includes(d.type ?? ""),
          ).length

          allUploaded = uploadedCount === requiredDocs.length
          totalRequiredDocs = requiredDocs.length

          requiredDocsDetailed = requiredDocs
            .map((id) => {
              const docMeta = allDocuments.find((d) => d.id === id)
              const uploadedDoc = docs.find((d) => d.type === id)
              return {
                id,
                title: docMeta?.displayName ?? id,
                uploaded: Boolean(uploadedDoc),
              }
            })
            .sort((a, b) => a.title.localeCompare(b.title))

          if (app.documents && Array.isArray(app.documents)) {
            uploadedDocsDetailed = docs
              .filter((d) => requiredDocs.includes(d.type ?? ""))
              .map((d) => {
                const docMeta = allDocuments.find((m) => m.id === d.type)
                return {
                  id: d.type ?? "",
                  title: docMeta?.displayName ?? d.type ?? "",
                  status: d.status.toLowerCase(),
                }
              })
              .sort((a, b) => a.title.localeCompare(b.title))
          }
        }

        if (stage === "vehicle-collection") {
          const bookings = (app.session_bookings || []) as BookingRow[]
          const vcBookings = selectActiveBookings(bookings, "vehicle_collection")
          vehicleCollectionBooked = vcBookings.length > 0
          const earliestBooking = findEarliestBooking(vcBookings)
          vehicleCollectionBookingDate = earliestBooking?.date ?? null
          vehicleCollectionAttended = earliestBooking?.attended ?? null
        }

        let trialBlockCount: number | undefined

        if (stage === "trial") {
          const trialBlocks = app.trial_blocks || []
          trialBlockCount = trialBlocks.length
        }

        let driverOnroadTestStatus: string | null = null
        let driverOnroadTestScore: number | null = null
        let driverOnroadTestResult: string | null = null

        if (stage === "driver-onroad-test") {
          const assessments = app.assessments || []
          const onroad = assessments.find((a) => a.type === "driver-onroad-test")

          if (!onroad || !onroad.started_at) {
            driverOnroadTestStatus = "not_started"
            driverOnroadTestScore = null
          } else if (onroad.started_at && !onroad.completed_at) {
            driverOnroadTestStatus = "in_progress"
            driverOnroadTestScore = null
          } else if (onroad.completed_at) {
            driverOnroadTestStatus = "completed"
            driverOnroadTestScore = onroad.score ?? null
            driverOnroadTestResult = onroad.result // "pass" | "fail"
          }
        }

        let orientationQuizStatus: string | null = null
        let orientationQuizScore: number | null = null
        let orientationQuizResult: string | null = null

        if (stage === "orientation-quiz") {
          const assessments = app.assessments || []
          const quiz = assessments.find((a) => a.type === "orientation-quiz")

          if (!quiz || !quiz.started_at) {
            orientationQuizStatus = "not_started"
            orientationQuizScore = null
            orientationQuizResult = null
          } else if (quiz.started_at && !quiz.completed_at) {
            orientationQuizStatus = "in_progress"
            orientationQuizScore = null
            orientationQuizResult = null
          } else if (quiz.completed_at) {
            orientationQuizStatus = "completed"
            orientationQuizScore = quiz.score ?? null
            orientationQuizResult = quiz.result
          }
        }

        let verifiedDocumentsCount = 0
        let totalDocumentsCount = 0

        if (stage === "orientation") {
          if (app.documents && Array.isArray(app.documents)) {
            totalDocumentsCount = app.documents.length
            verifiedDocumentsCount = app.documents.filter((doc) => doc.status === "verified").length
          }
        }

        let onboardedAt: string | null = null

        if (stage === "complete") {
          const history = app.stage_history || []
          const completeEntries = history.filter((h) => h.stage === "complete")

          if (completeEntries.length > 0) {
            const lastComplete = completeEntries.reduce(
              (latest, h) => {
                if (!latest) return h
                return new Date(h.moved_at) > new Date(latest.moved_at) ? h : latest
              },
              null as StageHistory | null,
            )

            onboardedAt = lastComplete?.moved_at ?? null
          }
        }

        return {
          id: app.id,
          application_id: app.application_id,
          airtablePersonId: app.person?.person_id ?? null,
          name: `${applicant.first_name || ""} ${applicant.last_name || ""}`.trim(),
          job_id: job.id,
          depot_id: depot.id,
          email: applicant.email || "",
          phone: applicant.phone_number || "",
          location: depot.name || "",
          jobPosting: job.title || "",
          hasOwnVehicle: applicant.has_electric_van || false,
          dataCollectionOnly: job.data_collection_only ?? null,
          stage: app.current_stage,
          contractSalary: app.contract_salary,
          status: app.status,
          segment: app.current_segment,
          submittedDate: app.created_at,
          changedStageAt: app.changed_stage_at,
          stageNote: app.stage_note ?? null,
          stageReason: app.stage_reason ?? null,
          allowedStages: job.stages?.map((stage) => stage.id.toLowerCase()) || [],
          orientationBooked,
          orientationBookingDate,
          orientationAttended,
          vehicleCollectionBooked,
          vehicleCollectionBookingDate,
          vehicleCollectionAttended,
          trialBlockCount,
          driverOnroadTestStatus,
          driverOnroadTestScore,
          driverOnroadTestResult,
          orientationQuizStatus,
          orientationQuizScore,
          orientationQuizResult,
          verifiedDocumentsCount,
          totalDocumentsCount,
          onboardedAt,
          requiredDocs,
          uploadedCount,
          verifiedCount,
          allUploaded,
          totalRequiredDocs,
          requiredDocsDetailed,
          uploadedDocsDetailed,
          personId: app.person_id,
        }
      })

      const totalCount = count

      const nextCursor = applications.length === limit ? pageParam + limit : null

      return {
        applications,
        nextCursor,
        totalCount: totalCount,
      }
    } catch (error) {
      console.error("Error in fetchApplicationsByStage:", error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Use react-query's useInfiniteQuery for infinite scrolling
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error } =
    useInfiniteQuery({
      queryKey: ["applications", stage, searchQuery, JSON.stringify(filters)],
      queryFn: fetchApplicationsByStage,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      initialPageParam: 0,
    })

  const applications = useMemo(() => {
    const allApplications = data?.pages.flatMap((page) => page.applications) || []
    return allApplications
  }, [data?.pages])

  const totalCount = useMemo(() => {
    return data?.pages?.[0]?.totalCount ?? 0
  }, [data?.pages])

  return {
    applications,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    loading,
  }
}

function getRequiredDocs(app: SupabaseRawRecord) {
  const jobData = Array.isArray(app.job) ? app.job[0] : app.job
  const jobStagesRaw = jobData?.stages ?? []

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let jobStages: any[] = []
  if (typeof jobStagesRaw === "string") {
    try {
      jobStages = JSON.parse(jobStagesRaw)
    } catch {
      jobStages = []
    }
  } else if (Array.isArray(jobStagesRaw)) {
    jobStages = jobStagesRaw
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const orientationIndex = jobStages.findIndex((s: any) => s.id === "orientation")
  if (orientationIndex === -1) return []

  const stagesBeforeOrientation = jobStages.slice(0, orientationIndex)

  const allSegments: string[] = []
  for (const stage of stagesBeforeOrientation) {
    if (stage.segments && Array.isArray(stage.segments)) {
      for (const seg of stage.segments) {
        if (seg.id) allSegments.push(seg.id)
      }
    }
  }

  const requiredDocs = allDocuments.filter((d) => allSegments.includes(d.segment)).map((d) => d.id)

  return [...new Set(requiredDocs)]
}
